import moment from 'moment';

// Normalizer function type: takes value and field name, returns normalized value
export type FieldNormalizer = (value: any, fieldName: string) => any;

export interface NormalizeFormValuesOptions {
  // Whether to process arrays recursively (default: true)
  processArrays?: boolean;
  // Custom normalizers for specific fields or patterns
  // Key can be exact field name or regex pattern string
  normalizers?: Record<string, FieldNormalizer | FieldNormalizer[]>;
  // Built-in date normalization (default: true)
  enableDateNormalization?: boolean;
  // Force process these fields for date normalization (only used when enableDateNormalization is true)
  strictDateFields?: string[];
}

/**
 * Universal form values normalizer with plugin-style architecture:
 * - Built-in date field normalization (can be disabled)
 * - Custom normalizers support for any field type
 * - Recursively processes nested objects AND arrays
 * - Pattern matching for flexible field rules
 *
 * @param values - The form values object to normalize
 * @param options - Configuration options
 * @returns Normalized form values
 *
 * @example
 * // Date normalization (default behavior)
 * normalizeFormValues({ invoice_date: "2024-01-01" })
 * // => { invoice_date: moment("2024-01-01") }
 *
 * @example
 * // Custom normalizers for specific fields
 * normalizeFormValues(
 *   { email: "  USER@EXAMPLE.COM  ", mbl_no: "  hlcu123  " },
 *   {
 *     normalizers: {
 *       email: (val) => val?.trim().toLowerCase(),
 *       mbl_no: (val) => val?.trim().toUpperCase(),
 *     }
 *   }
 * )
 * // => { email: "user@example.com", mbl_no: "HLCU123" }
 *
 * @example
 * // Pattern-based normalizers with regex
 * normalizeFormValues(
 *   { container_no: "  abcd1234  ", tracking_no: "  xyz999  " },
 *   {
 *     normalizers: {
 *       '.*_no$': (val) => val?.trim().toUpperCase(), // all fields ending with _no
 *     }
 *   }
 * )
 * // => { container_no: "ABCD1234", tracking_no: "XYZ999" }
 *
 * @example
 * // Multiple normalizers (chain processing)
 * normalizeFormValues(
 *   { description: "  HELLO WORLD  " },
 *   {
 *     normalizers: {
 *       description: [
 *         (val) => val?.trim(),           // first: trim
 *         (val) => val?.toLowerCase(),    // then: lowercase
 *       ]
 *     }
 *   }
 * )
 * // => { description: "hello world" }
 *
 * @example
 * // Disable built-in date normalization
 * normalizeFormValues(
 *   { invoice_date: "2024-01-01", email: "  test@test.com  " },
 *   {
 *     enableDateNormalization: false,
 *     normalizers: { email: (val) => val?.trim() }
 *   }
 * )
 * // => { invoice_date: "2024-01-01", email: "test@test.com" }
 *
 * @example
 * // Complex real-world scenario
 * normalizeFormValues(
 *   {
 *     invoice_date: "2024-01-01",
 *     email: "  USER@EXAMPLE.COM  ",
 *     mbl_no: "  hlcu123456  ",
 *     amount: "1234.56",
 *     items: [
 *       { due_date: "2024-02-01", description: "  item 1  " },
 *       { due_date: "2024-03-01", description: "  item 2  " },
 *     ]
 *   },
 *   {
 *     processArrays: true,
 *     enableDateNormalization: true,
 *     normalizers: {
 *       email: (val) => val?.trim().toLowerCase(),
 *       '.*_no$': (val) => val?.trim().toUpperCase(),
 *       amount: (val) => parseFloat(val) || 0,
 *       description: (val) => val?.trim(),
 *     }
 *   }
 * )
 * // => {
 * //   invoice_date: moment("2024-01-01"),
 * //   email: "user@example.com",
 * //   mbl_no: "HLCU123456",
 * //   amount: 1234.56,
 * //   items: [
 * //     { due_date: moment("2024-02-01"), description: "item 1" },
 * //     { due_date: moment("2024-03-01"), description: "item 2" },
 * //   ]
 * // }
 */
export const normalizeFormValues = <T extends Record<string, any>>(
  values: T,
  options: NormalizeFormValuesOptions = {},
): T => {
  const {
    processArrays = true,
    normalizers = {},
    enableDateNormalization = true,
    strictDateFields = [],
  } = options;

  if (!values || typeof values !== 'object') return values;

  // Built-in date field detection
  const isDateLikeKey = (key: string) => {
    if (!enableDateNormalization) return false;
    if (strictDateFields.includes(key)) return true;
    return /(_date|_at|date)$/i.test(key);
  };

  // Built-in date normalizer
  const normalizeDateValue = (val: any): any => {
    if (typeof val !== 'string') return val;
    const m = moment(
      val,
      ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY-MM-DD HH:mm:ss', 'YYYY/MM/DD HH:mm:ss', moment.ISO_8601],
      true,
    );
    return m.isValid() ? m : val;
  };

  // Find matching custom normalizers for a field
  const getCustomNormalizers = (fieldName: string): FieldNormalizer[] => {
    const matched: FieldNormalizer[] = [];

    for (const [pattern, normalizer] of Object.entries(normalizers)) {
      // Exact match
      if (pattern === fieldName) {
        matched.push(...(Array.isArray(normalizer) ? normalizer : [normalizer]));
        continue;
      }

      // Pattern match (regex)
      try {
        if (new RegExp(pattern, 'i').test(fieldName)) {
          matched.push(...(Array.isArray(normalizer) ? normalizer : [normalizer]));
        }
      } catch {
        // Invalid regex, skip
      }
    }

    return matched;
  };

  const normalizeValue = (val: any, key: string): any => {
    if (val == null) return val;

    // Skip already moment objects
    if (moment.isMoment(val)) return val;

    // Process arrays recursively (if enabled)
    if (Array.isArray(val)) {
      if (!processArrays) return val;

      return val.map(item => {
        if (item && typeof item === 'object' && !moment.isMoment(item)) {
          return normalizeFormValues(item, options);
        }
        return item;
      });
    }

    // Recursively process nested objects
    if (typeof val === 'object' && !moment.isMoment(val)) {
      return normalizeFormValues(val, options);
    }

    let result = val;

    // Apply custom normalizers first
    const customNormalizers = getCustomNormalizers(key);
    for (const normalizer of customNormalizers) {
      result = normalizer(result, key);
    }

    // Apply built-in date normalization if enabled and field matches
    if (enableDateNormalization && isDateLikeKey(key)) {
      result = normalizeDateValue(result);
    }

    return result;
  };

  const result: Record<string, any> = {};
  for (const [key, val] of Object.entries(values)) {
    result[key] = normalizeValue(val, key);
  }

  return result as T;
};

/**
 * Legacy method - converts date fields to moment objects
 * Now uses normalizeFormValues internally
 * @deprecated Use normalizeFormValues instead for better functionality
 */
export function transformDateFieldsToMoment(values: Record<string, any>) {
  return normalizeFormValues(values);
}

// Common normalizer presets for reuse
export const CommonNormalizers = {
  // Trim whitespace
  trim: (val: any) => (typeof val === 'string' ? val.trim() : val),

  // Convert to uppercase
  uppercase: (val: any) => (typeof val === 'string' ? val.toUpperCase() : val),

  // Convert to lowercase
  lowercase: (val: any) => (typeof val === 'string' ? val.toLowerCase() : val),

  // Trim and uppercase (common for codes/numbers)
  trimUppercase: (val: any) => (typeof val === 'string' ? val.trim().toUpperCase() : val),

  // Trim and lowercase (common for emails)
  trimLowercase: (val: any) => (typeof val === 'string' ? val.trim().toLowerCase() : val),

  // Parse to float
  parseFloat: (val: any) => {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? val : parsed;
  },

  // Parse to integer
  parseInt: (val: any) => {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? val : parsed;
  },

  // Convert to boolean
  toBoolean: (val: any) => {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') {
      return val.toLowerCase() === 'true' || val === '1';
    }
    return val === 1;
  },

  // Remove all whitespace
  removeWhitespace: (val: any) => (typeof val === 'string' ? val.replace(/\s+/g, '') : val),

  // Normalize phone number (remove non-digits)
  normalizePhone: (val: any) => (typeof val === 'string' ? val.replace(/\D/g, '') : val),
};
